/**
 * The four reads of the hot translations (#4038) as screens: the list of keys of a language, the history of one key,
 * the pending drafts and the publications. The shapes (queries, data) are `contracts/admin/translations.types.ts`
 * (#4034); each screen only wraps its data with `freshness`. All four are super-admin only and depend on the topic
 * `admin.translations`, which a publication moves.
 */

import { z } from 'zod/v4'

import {
  translationHistoryDataSchema,
  translationHistoryQuerySchema,
  translationListDataSchema,
  translationListQuerySchema,
  translationPendingDraftsDataSchema,
  translationPendingDraftsQuerySchema,
  translationPublicationsDataSchema,
  translationPublicationsQuerySchema,
} from '../../contracts/admin/translations.types.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'

export const adminTranslationsScreen = defineScreen({
  id: 'admin.translations',
  path: '/api/screens/admin/translations',
  access: 'super_admin',
  query: translationListQuerySchema,
  response: z.strictObject({ ...translationListDataSchema.shape, freshness: freshnessSchema }),
  dependsOn: ['admin.translations'],
  // `bytes`: the largest page of 200 rows in the WORST case (a published text and a draft on every row, the full
  // `{ success, data: { ..., freshness } }` envelope, walking the cursor over the real catalogues) measured 216,526 B
  // in `es` (reference `en`) and 195,027 B in `ar` (reference `es`); budget = 216,526 x 1.25, rounded up to thousands.
  // A page without overrides is about 124 kB. The three statements: the override rows of the language, the authors and the publication dates.
  budget: { requests: 1, statements: 3, bytes: 271_000, p95Ms: 150 },
  summary:
    'Translations: one page of keys of a language, counts, coverage, pending drafts and freshness',
})
export type TranslationsScreenQuery = ScreenQuery<typeof adminTranslationsScreen>
export type TranslationsScreenData = ScreenData<typeof adminTranslationsScreen>

export const adminTranslationHistoryScreen = defineScreen({
  id: 'admin.translations.history',
  path: '/api/screens/admin/translations/history',
  access: 'super_admin',
  query: translationHistoryQuerySchema,
  response: z.strictObject({ ...translationHistoryDataSchema.shape, freshness: freshnessSchema }),
  dependsOn: ['admin.translations'],
  budget: { requests: 1, statements: 3, bytes: 40_000, p95Ms: 150 },
  summary:
    'Translations › History: the newest revisions of one key, their real total and freshness',
})
export type TranslationHistoryScreenQuery = ScreenQuery<typeof adminTranslationHistoryScreen>
export type TranslationHistoryScreenData = ScreenData<typeof adminTranslationHistoryScreen>

export const adminTranslationDraftsScreen = defineScreen({
  id: 'admin.translations.drafts',
  path: '/api/screens/admin/translations/drafts',
  access: 'super_admin',
  query: translationPendingDraftsQuerySchema,
  response: z.strictObject({
    ...translationPendingDraftsDataSchema.shape,
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.translations'],
  budget: { requests: 1, statements: 2, bytes: 60_000, p95Ms: 150 },
  summary:
    'Translations › Drafts: the drafts waiting to be published, their warnings and freshness',
})
export type TranslationDraftsScreenQuery = ScreenQuery<typeof adminTranslationDraftsScreen>
export type TranslationDraftsScreenData = ScreenData<typeof adminTranslationDraftsScreen>

export const adminTranslationPublicationsScreen = defineScreen({
  id: 'admin.translations.publications',
  path: '/api/screens/admin/translations/publications',
  access: 'super_admin',
  query: translationPublicationsQuerySchema,
  response: z.strictObject({
    ...translationPublicationsDataSchema.shape,
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.translations'],
  budget: { requests: 1, statements: 2, bytes: 40_000, p95Ms: 150 },
  summary:
    'Translations › Publications: one page of publications by cursor, newest first, and freshness',
})
export type TranslationPublicationsScreenQuery = ScreenQuery<
  typeof adminTranslationPublicationsScreen
>
export type TranslationPublicationsScreenData = ScreenData<
  typeof adminTranslationPublicationsScreen
>
